import { applyMove } from './applyMove.js';
import { fenToPosition, parseFen } from './fen.js';
import { generateLegalMoves } from './legalMoves.js';
import { classifyPosition, type Outcome } from './outcome.js';
import { FIDE, withReplayLimits, type RuleSet } from './rules/index.js';
import { findUciMove, moveToUci } from './uci.js';
import { FLAG_EN_PASSANT, type Move, type Position } from './types.js';

/**
 * Repetition key: placement, side to move, castling rights, and the
 * en-passant square only when a capture there is actually legal — FIDE treats
 * positions as identical when the same moves are available.
 */
function repetitionKey(pos: Position, legal: readonly Move[]): string {
  const ep = legal.some((m) => (m.flags & FLAG_EN_PASSANT) !== 0) ? pos.epSquare : -1;
  return `${pos.board.join(',')}|${pos.turn}|${pos.castling}|${ep}`;
}

/**
 * A game in progress: the current position, how it got there, and whether it
 * has ended. Immutable — `play` returns a new state. The bot searches raw
 * `Position`s instead; this class is for the game's own bookkeeping.
 */
export class GameState {
  readonly rules: RuleSet;
  readonly startFen: string;
  readonly position: Position;
  readonly moves: readonly Move[];
  readonly legalMoves: readonly Move[];
  readonly outcome: Outcome | null;
  private readonly keys: readonly string[];

  private constructor(
    rules: RuleSet,
    startFen: string,
    position: Position,
    moves: readonly Move[],
    keys: readonly string[],
    legal: readonly Move[],
    concluded: Outcome | null,
  ) {
    this.rules = rules;
    this.startFen = startFen;
    this.position = position;
    this.moves = moves;
    this.keys = keys;
    this.outcome = concluded ?? classifyPosition(position, legal.length, this.repetitions, rules);
    this.legalMoves = this.outcome === null ? legal : [];
  }

  /** Builds a state that has just reached `position`, appending its repetition key. */
  private static reach(rules: RuleSet, startFen: string, position: Position, moves: readonly Move[], priorKeys: readonly string[]): GameState {
    const legal = generateLegalMoves(position);
    return new GameState(rules, startFen, position, moves, [...priorKeys, repetitionKey(position, legal)], legal, null);
  }

  static start(rules: RuleSet = FIDE): GameState {
    return GameState.fromFen(rules.startFen, rules);
  }

  /** A game from a position known to be valid. Throws on a malformed FEN. */
  static fromFen(fen: string, rules: RuleSet = FIDE): GameState {
    return GameState.reach(rules, fen, fenToPosition(fen), [], []);
  }

  /** Validating constructor for user input (pasted FEN). */
  static tryFromFen(fen: string, rules: RuleSet = FIDE): GameState | string {
    const parsed = parseFen(fen);
    return parsed.ok ? GameState.fromFen(fen.trim(), rules) : parsed.error;
  }

  /**
   * Rebuild a recorded game from its UCI moves; null if any move is illegal.
   * Recorded history was already played, so it replays under FIDE's
   * automatic draw limits (see `withReplayLimits`) — a game that went on past
   * an unclaimed threefold still rebuilds — and the result plays on under `rules`.
   */
  static fromUciMoves(startFen: string, uciMoves: readonly string[], rules: RuleSet = FIDE): GameState | null {
    let state = GameState.fromFen(startFen, withReplayLimits(rules));
    for (const uci of uciMoves) {
      const next = state.playUci(uci);
      if (next === null) return null;
      state = next;
    }
    return state.withRules(rules);
  }

  get turn(): Position['turn'] {
    return this.position.turn;
  }

  get isOver(): boolean {
    return this.outcome !== null;
  }

  get lastMove(): Move | undefined {
    return this.moves[this.moves.length - 1];
  }

  /** How many times the current position has occurred, including now. */
  get repetitions(): number {
    const current = this.keys[this.keys.length - 1];
    return this.keys.filter((k) => k === current).length;
  }

  get uciMoves(): string[] {
    return this.moves.map(moveToUci);
  }

  /**
   * The state after a legal move. Matched by value (from, to, promotion), so a
   * move built elsewhere — a replay, a structured clone from the bot worker —
   * is accepted. Throws when no legal move matches.
   */
  play(move: Move): GameState {
    const legal = this.legalMoves.find((m) => m.from === move.from && m.to === move.to && m.promotion === move.promotion);
    if (legal === undefined) throw new Error('GameState.play: move is not legal here');
    return GameState.reach(this.rules, this.startFen, applyMove(this.position, legal), [...this.moves, legal], this.keys);
  }

  /** The state after a UCI move, or null if it is not legal (e.g. from the wire). */
  playUci(uci: string): GameState | null {
    const move = findUciMove(this.legalMoves, uci);
    return move === undefined ? null : this.play(move);
  }

  /**
   * The same game under other rules. History is kept as played; only the
   * current position is judged again (a game already over stays over).
   */
  withRules(rules: RuleSet): GameState {
    const legal = this.outcome === null ? generateLegalMoves(this.position) : [];
    return new GameState(rules, this.startFen, this.position, this.moves, this.keys, legal, this.outcome);
  }

  /** End the game for a reason outside the board: resignation, agreement, flag. */
  conclude(outcome: Outcome): GameState {
    return new GameState(this.rules, this.startFen, this.position, this.moves, this.keys, [], outcome);
  }
}
